import {
  Avatar,
  Col,
  Flex,
  Form,
  FormProps,
  Input,
  Upload,
  UploadFile,
  UploadProps,
  Button as AntButton,
  Select,
} from "antd";
import { Exercise } from "../../types/Entities";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getBase64EncodedImage } from "../../utils";
import { isVideoSrc } from "../../utils/mediaKind";
import { v4 } from "uuid";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faSave, faUpload } from "@fortawesome/free-solid-svg-icons";
import TextArea from "antd/es/input/TextArea";
import { Option } from "../../types/generics";
import { TailSpin } from "react-loader-spinner";

type Props = {
  exercise?: Exercise;
  onSubmit?: (values: any, file?: any) => void;
  muscleGroups?: Option[];
  motorPatterns?: Option[];
  onClose?: () => void;
  loading?: boolean;
};

const formLayout: FormProps = {
  layout: "vertical",
  colon: false,
};

export default function ExerciseForm({
  exercise,
  onSubmit,
  onClose,
  muscleGroups,
  motorPatterns,
  loading,
}: Props) {
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState("");
  // Whether the current media preview is a video (renders inline <video>
  // instead of the image Avatar).
  const [mediaIsVideo, setMediaIsVideo] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const uploadProps = useMemo<UploadProps>(
    () => ({
      name: "imageUrl",
      // The "Immagine" of an exercise may be a picture, an animated GIF, or a
      // short video.
      accept: "image/*,video/*",
      beforeUpload: (file) => {
        const isMedia =
          file.type.startsWith("image/") || file.type.startsWith("video/");
        if (!isMedia) {
          showToast(
            "file-type-error",
            "Puoi caricare solo immagini o video",
            null,
            "warning"
          );
          return Upload.LIST_IGNORE;
        }
        return false;
      },
      onChange: async ({ fileList }) => {
        if (fileList.length) {
          const latestFile = fileList[fileList.length - 1];
          const raw = latestFile.originFileObj!;
          const isVideo = (raw.type ?? "").startsWith("video/");
          setMediaIsVideo(isVideo);
          // Video: an object URL streams the clip for the inline preview.
          // Image / GIF: a data URL feeds the Avatar preview.
          const src = isVideo
            ? URL.createObjectURL(raw)
            : await getBase64EncodedImage(raw);
          setImageUrl(src);
          form.setFieldValue("img", src);
          setFileList([latestFile]);
        } else {
          setImageUrl("");
          setMediaIsVideo(false);
          form.setFieldValue("img", null);
          setFileList([]);
        }
      },
    }),
    [form, showToast]
  );

  useEffect(() => {
    form.setFieldsValue(exercise);
    if (exercise && exercise.signedUrl) {
      const file: UploadFile = {
        uid: v4(),
        name: exercise.name,
        status: "done",
        url: exercise.signedUrl,
      };
      setFileList([file]);
      setImageUrl(exercise.signedUrl);
      setMediaIsVideo(isVideoSrc(exercise.signedUrl));
    }
  }, [form, exercise]);

  const handleSubmit = useCallback((values: any) => {
    onSubmit?.(values, fileList?.[0]?.originFileObj);
  }, [fileList, onSubmit]);

  return (
    <Form
      className="compact-form"
      form={form}
      {...formLayout}
      onFinish={handleSubmit}
    >
      <Modal.Body>
        <Flex align="center">
          <Col span={6} className="text-center pe-2">
            {mediaIsVideo && imageUrl ? (
              <video
                src={imageUrl}
                muted
                loop
                autoPlay
                playsInline
                className="border"
                style={{
                  height: 80,
                  width: 80,
                  objectFit: "cover",
                  borderRadius: "50%",
                  background: "#1f1f1f",
                }}
              />
            ) : (
              <Avatar
                src={imageUrl || undefined}
                icon={<FontAwesomeIcon icon={faImage} size="xl" />}
                className="border"
                style={{ height: 80, width: 80 }}
              />
            )}
          </Col>
          <Col flex="auto">
            <Form.Item
              name="name"
              label="Nome Esercizio"
              rules={[
                {
                  required: true,
                  message: "Il nome dell'esercizio è obbligatorio",
                },
              ]}
            >
              <Input placeholder="Nome Esercizio" />
            </Form.Item>
            <Form.Item name="img" label="Immagine">
              <Upload {...uploadProps} fileList={fileList}>
                <AntButton icon={<FontAwesomeIcon icon={faUpload} />}>
                  Carica Immagine
                </AntButton>
              </Upload>
            </Form.Item>
          </Col>
        </Flex>

        <Form.Item name="description" label="Descrizione">
          <TextArea cols={24} rows={5} placeholder="Descrizione Esercizio" />
        </Form.Item>

        <Form.Item name="mediaLink" label="Link Video">
          <Input placeholder="Link Video..." />
        </Form.Item>

        <Form.Item name="muscleGroupId" label="Gruppo Muscolare">
          <Select
            placeholder="Seleziona il gruppo muscolare"
            allowClear
            className="w-100"
          >
            {(muscleGroups || []).map((option, index) => (
              <Select.Option
                key={index}
                value={option.value}
                label={option.label}
              >
                <span>{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="motorPatternId" label="Pattern Motorio">
          <Select
            placeholder="Seleziona il pattern motorio"
            allowClear
            className="w-100"
          >
            {(motorPatterns || []).map((option, index) => (
              <Select.Option
                key={index}
                value={option.value}
                label={option.label}
              >
                <span>{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button
          variant="light"
          size="sm"
          className="border"
          onClick={onClose}
          disabled={loading}
        >
          {loading ? (
            <TailSpin width={15} height={15} color="#9c1419" />
          ) : (
            <span>Chiudi</span>
          )}
        </Button>
        <Button type="submit" variant="success" size="sm" disabled={loading}>
          {loading ? (
            <TailSpin width={15} height={15} color="#9c1419" />
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} />
              <span className="ms-2">Salva</span>
            </>
          )}
        </Button>
      </Modal.Footer>
    </Form>
  );
}
